import { Component, ReactNode } from "react";
import { readLoginStateCookie } from "../../../auth/cookie-utils";
import { KuikaAppManager } from "../../../shared/utilty/kuika-app-manager";
import { reserveTwinChatFabSlot } from "../floating-stack";
import { isPageChatActive, subscribePageChat } from "./twin-chat-presence";
import { getConfiguredTwinUrl, isTwinChatEnabled, mountTwinChatToBody } from "./twin-mfe-mount";

// How often to re-check login state before mounting the chat. Once mounted (or once
// logged in), the login poll stops; later changes come from the page-chat
// subscription. A cookie read is cheap; this just lets a login that happens after
// this widget mounts still bring up the chat FAB.
const LOGIN_POLL_MS: number = 1500;

interface TwinChatWidgetProps {}

interface TwinChatWidgetState {}

/**
 * Global Twin chat — mounts Twin's chat MFE in FAB mode app-wide when the config
 * toggle `twinChatEnabled` is on. The Twin element renders its own floating launcher
 * + panel; we mount it (once) onto <body> via mountTwinChatToBody. Mounted once at
 * the app root (bootstrapper), gated on login state so it shows on every screen
 * except the pre-login auth screens. Renders no React DOM — the chat lives directly
 * under <body>.
 *
 * Precedence: when a screen explicitly drops the per-screen TwinChatLive element, it
 * claims the page (twin-chat-presence) and this global widget stands down so there's
 * never a double chat — the page-specific one wins.
 */
export class TwinChatWidget extends Component<TwinChatWidgetProps, TwinChatWidgetState> {
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private isWidgetMounted: boolean = false;

  private isMounting: boolean = false;

  // The <body>-level host of the mounted chat, removed on cleanup.
  private host: HTMLElement | null = null;

  // Reserves the chat FAB's corner footprint so FloatButtons stack above it.
  private unreserveFabSlot: (() => void) | null = null;

  private unsubscribePageChat: (() => void) | null = null;

  componentDidMount(): void {
    this.isWidgetMounted = true;

    const isDesignTime: boolean = window.kuika?.isDesignTime === true;
    if (isDesignTime) {
      return;
    }

    // Global FAB is gated by the toggle; without it (or without Twin configured),
    // chat is only available per-screen via the Twin Chat design element.
    if (!isTwinChatEnabled() || !getConfiguredTwinUrl()) {
      return;
    }

    // React to a per-screen chat element appearing/disappearing.
    this.unsubscribePageChat = subscribePageChat((): void => {
      void this.reconcile();
    });

    void this.reconcile();
    this.pollTimer = setInterval((): void => {
      void this.reconcile();
    }, LOGIN_POLL_MS);
  }

  componentWillUnmount(): void {
    this.isWidgetMounted = false;
    this.clearPoll();
    if (this.unsubscribePageChat) {
      this.unsubscribePageChat();
      this.unsubscribePageChat = null;
    }
    this.teardownChat();
  }

  private clearPoll = (): void => {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  private isLoggedIn = (): boolean => {
    return readLoginStateCookie("is_logged_in", KuikaAppManager.getLoginStateCookieKey()) === "1";
  };

  private teardownChat = (): void => {
    if (this.unreserveFabSlot) {
      this.unreserveFabSlot();
      this.unreserveFabSlot = null;
    }
    if (this.host && this.host.parentNode) {
      this.host.parentNode.removeChild(this.host);
    }
    this.host = null;
  };

  // Brings the global chat in line with current state: shown only when the user is
  // logged in AND no per-screen chat element is claiming the page. Called on mount,
  // on each login poll, and whenever page-chat presence changes.
  private reconcile = async (): Promise<void> => {
    if (!this.isWidgetMounted) {
      return;
    }
    // Chat is a logged-in widget — keep polling until the user has a session (and so
    // the pre-login auth screens never show it).
    const isLoggedIn: boolean = this.isLoggedIn();
    if (!isLoggedIn) {
      return;
    }

    // A page-specific chat takes precedence — stand down if one is present. The login
    // poll keeps running so we re-mount once the page chat goes away.
    if (isPageChatActive()) {
      this.teardownChat();
      return;
    }

    if (this.host || this.isMounting) {
      return;
    }

    this.isMounting = true;
    try {
      const host: HTMLElement = await mountTwinChatToBody();
      // A page chat may have claimed the page (or we unmounted) during the async load.
      const pageChatClaimedPage: boolean = isPageChatActive();
      if (!this.isWidgetMounted || pageChatClaimedPage) {
        if (host.parentNode) {
          host.parentNode.removeChild(host);
        }
        return;
      }
      this.host = host;
      this.unreserveFabSlot = reserveTwinChatFabSlot();
      // Steady state — stop the login poll; further changes (a page chat appearing or
      // disappearing) arrive via the subscription.
      this.clearPoll();
    } catch {
      // Token not available yet / not configured — fail closed. The poll (until
      // login) or the page-chat subscription will retry reconciliation.
    } finally {
      this.isMounting = false;
    }
  };

  render(): ReactNode {
    return null;
  }
}
